import React, { useEffect, useState } from 'react'
import { Eye, EyeOff, Lock, User } from 'lucide-react'
import { login } from '../services/api'
import LogoFrotaWeb from '../components/LogoFrotaWeb'
import loginBg from '../assets/login-bg.png'

/* Tela espelhada na do FrotaTrack (modules/auth/frontend/pages/Login.tsx):
   mesmo degradê de fundo, destaques à esquerda no desktop, card de vidro
   centralizado com a marca acima, campos escuros com ícone à esquerda, botão
   laranja em degradê e a listra azul→laranja→azul no rodapé.

   O fundo é a mesma receita do TrackingMapOverlay.tsx de lá, valor por valor —
   duas tentativas minhas de inventar deram errado (véu navy por cima = escuro
   demais; `mix-blend-mode: screen` = claro e lavado demais). A receita:

   1. degradê de marca na raiz;
   2. três brilhos radiais, que dão profundidade só com luz — sem desenho;
   3. a arte num <svg>, com `opacity 0.62` e `brightness(1.4) saturate(1.05)`,
      composição NORMAL (nada de blend mode);
   4. uma MÁSCARA horizontal que dissolve a arte: ela nem existe na faixa
      esquerda e vai aparecendo até ficar cheia. É o que dá a área limpa pros
      destaques sem criar borda nem retângulo visível;
   5. um escurecimento radial no centro, pra deixar o formulário legível.

   A diferença é só a ancoragem: `xMaxYMid` em vez de `xMidYMid`, porque os
   módulos da nossa arte ficam encostados na direita e é esse lado que precisa
   sobreviver ao corte quando a tela é mais estreita que a imagem. */

/* Dimensões reais de login-bg.png — o <svg> precisa delas no viewBox pra máscara
   ficar em cima da arte, e não em cima de um retângulo arbitrário. */
const BG_W = 1824
const BG_H = 862

const DESTAQUES = [
  {
    title: 'Tudo em um só sistema',
    desc: 'Frota, fiscal e RH integrados, sem planilha solta.',
    icon: (
      <svg viewBox="0 0 24 24" className="w-11 h-11" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 3 3 8l9 5 9-5-9-5Z" />
        <path d="M3 12l9 5 9-5" stroke="#f07820" />
        <path d="M3 16l9 5 9-5" />
      </svg>
    ),
  },
  {
    title: 'Emissão fiscal sem dor de cabeça',
    desc: 'NF-e, CT-e OS e NFS-e direto do sistema.',
    icon: (
      <svg viewBox="0 0 24 24" className="w-11 h-11" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 2.8h9.5L19 6.3V21a.8.8 0 0 1-.8.8H6a.8.8 0 0 1-.8-.8V3.6A.8.8 0 0 1 6 2.8Z" />
        <path d="M8.3 9h7.4M8.3 12.3h7.4" />
        <path d="M8.3 15.6h4.4" stroke="#f07820" />
      </svg>
    ),
  },
  {
    title: 'Decisões com dados reais',
    desc: 'Consultas e relatórios sempre atualizados.',
    icon: (
      <svg viewBox="0 0 24 24" className="w-11 h-11" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 19v-6M10 19V9M16 19v-4" />
        <path d="M3 8.5 9 5l5 2.7 7-4" stroke="#f07820" />
      </svg>
    ),
  },
  {
    title: 'Acesso sob controle',
    desc: 'Cada usuário vê só o que precisa ver.',
    icon: (
      <svg viewBox="0 0 24 24" className="w-11 h-11" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 3.2 19 6v5.5c0 4.5-3 7.6-7 8.9-4-1.3-7-4.4-7-8.9V6l7-2.8Z" />
        <path d="m9 12 2 2 4-4" stroke="#f07820" />
      </svg>
    ),
  },
]

interface Props {
  onLogin: (user: string, tiplib: number | null, perfil: string) => void
}

export default function LoginPage({ onLogin }: Props) {
  const [usuario, setUsuario] = useState('')
  const [senha, setSenha] = useState('')
  const [mostrarSenha, setMostrarSenha] = useState(false)
  const [erro, setErro] = useState(() => sessionStorage.getItem('frotaweb_aviso_login') || '')
  useEffect(() => { sessionStorage.removeItem('frotaweb_aviso_login') }, []) // aviso de usuário desativado aparece uma vez só
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErro('')

    if (!usuario || !senha) {
      setErro('Informe o usuário e a senha.')
      return
    }

    setLoading(true)
    try {
      const data = await login(usuario, senha)
      onLogin(data.nome, data.tiplib, data.perfil ?? 'comum')
    } catch (err: any) {
      setErro(err?.response?.data?.error || 'Usuário ou senha incorretos.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden select-none"
      /* Mesmo degradê do FrotaTrack, sem alteração. */
      style={{ background: 'linear-gradient(120deg, #1a4c78 0%, #1f5688 44%, #163a5c 100%)' }}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none select-none" aria-hidden="true">
        {/* 2. Profundidade só com luz — mesmos valores do FrotaTrack */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(48% 54% at 16% 22%, rgba(120,176,224,0.22), transparent 60%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(52% 56% at 34% 88%, rgba(11,44,78,0.42), transparent 62%)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(40% 40% at 40% 52%, rgba(90,148,205,0.12), transparent 58%)' }} />

        {/* 3 + 4. Arte dos módulos, dissolvendo da direita para o centro */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox={`0 0 ${BG_W} ${BG_H}`}
          preserveAspectRatio="xMaxYMid slice"
        >
          <defs>
            <linearGradient id="fw-fade" x1="0" y1="0" x2={BG_W} y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0"    stopColor="#fff" stopOpacity="0" />
              <stop offset="0.42" stopColor="#fff" stopOpacity="0" />
              <stop offset="0.66" stopColor="#fff" stopOpacity="1" />
              <stop offset="1"    stopColor="#fff" stopOpacity="1" />
            </linearGradient>
            <mask id="fw-img-mask">
              <rect x="0" y="0" width={BG_W} height={BG_H} fill="url(#fw-fade)" />
            </mask>
          </defs>

          <image
            href={loginBg}
            x="0" y="0" width={BG_W} height={BG_H}
            mask="url(#fw-img-mask)"
            opacity="0.62"
            style={{ filter: 'brightness(1.4) saturate(1.05)' }}
          />
        </svg>

        {/* 5. Escurecimento atrás do centro, no mesmo tom → contínuo, sem borda */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 44% 58% at 50% 50%, rgba(12,32,54,0.28) 0%, rgba(12,32,54,0.08) 48%, transparent 72%)',
          }}
        />
      </div>

      {/* Destaques — só no desktop. Distância do card igual à do FrotaTrack. */}
      <div className="hidden lg:flex flex-col gap-8 absolute left-[13%] xl:left-[15%] top-1/2 -translate-y-1/2 z-10 max-w-[300px]">
        {DESTAQUES.map(d => (
          <div key={d.title} className="flex items-center gap-4">
            <span className="shrink-0 text-[#5fb0ea]">{d.icon}</span>
            <div>
              <h2 className="text-white text-[17px] font-semibold leading-tight">{d.title}</h2>
              <p className="text-white/55 text-sm mt-1 leading-snug">{d.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Formulário */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">

          {/* Marca + assinatura, como no FrotaTrack. A assinatura já existe dentro
              do PNG, mas ali ela é miúda demais pra ler; repetida como linha de
              texto com entreletra aberta, ela cumpre a função — e é exatamente a
              composição do login de lá ("RASTREAMENTO VEICULAR"). */}
          <div className="flex flex-col items-center mb-8">
            {/* Sombra projetada: o azul do wordmark ("Frota") é vizinho do azul do
                fundo e sem isso ele quase desaparece — só o "Web" laranja se lia. */}
            <LogoFrotaWeb height={96} className="drop-shadow-[0_4px_18px_rgba(4,18,34,0.6)]" />
            <p className="text-white/40 text-xs tracking-[0.25em] uppercase mt-2">
              Gestão Empresarial
            </p>
          </div>

          <div
            className="glass rounded-2xl p-8"
            style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.08)' }}
          >
            <h2 className="text-white text-xl font-semibold mb-1">Acessar Sistema</h2>
            <p className="text-white/40 text-sm mb-7">Insira suas credenciais para continuar</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-white/60 text-xs font-medium mb-2 tracking-wide uppercase">
                  Usuário
                </label>
                <div className="relative">
                  <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/60 z-10 pointer-events-none" />
                  <input
                    type="text"
                    value={usuario}
                    onChange={e => { setUsuario(e.target.value); setErro('') }}
                    placeholder="Digite seu usuário"
                    autoFocus
                    autoComplete="username"
                    className="input-dark pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/60 text-xs font-medium mb-2 tracking-wide uppercase">
                  Senha
                </label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/60 z-10 pointer-events-none" />
                  <input
                    type={mostrarSenha ? 'text' : 'password'}
                    value={senha}
                    onChange={e => { setSenha(e.target.value); setErro('') }}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="input-dark pl-10 pr-11"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarSenha(v => !v)}
                    tabIndex={-1}
                    aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                    className="icon-btn-dark absolute right-3.5 top-1/2 -translate-y-1/2 z-10"
                  >
                    {mostrarSenha ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Erro inline. Não usa `.login-error` (caixa clara) porque aqui o
                  fundo é escuro — lá ficaria um retângulo branco no meio do card. */}
              {erro && <p className="text-red-300 text-xs -mt-1">{erro}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-3.5 rounded-xl font-semibold text-white text-sm tracking-wide
                           transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                           hover:shadow-glow-orange active:scale-[.98]"
                /* `border`/`appearance` explícitos: com o preflight do Tailwind
                   desligado, o <button> herda a borda padrão do navegador. */
                style={{
                  border: 'none',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  background: loading
                    ? 'rgba(240,120,32,0.6)'
                    : 'linear-gradient(135deg, #f07820 0%, #c45e10 100%)',
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner />
                    Entrando...
                  </span>
                ) : 'Entrar'}
              </button>
            </form>
          </div>

          <p className="text-center text-white/20 text-xs mt-6">
            © {new Date().getFullYear()} FrotaWeb · Todos os direitos reservados
          </p>
        </div>
      </div>

      {/* Listra decorativa do rodapé — idêntica à do FrotaTrack */}
      <div
        className="absolute bottom-0 left-0 right-0 h-1 z-10"
        style={{ background: 'linear-gradient(90deg, #1a6ea8, #f07820, #1a6ea8)' }}
      />
    </div>
  )
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}
